import { Lead } from "../leads/types/LeadTypes"
import {
  filterLeadsByTimeFrame,
  DEFAULT_TIMEFRAME,
  validateTimeFrame,
  parseLeadDate,
} from "../leads/utils/dateFilters"
import { calculateTotalAdSpend } from "@/utils/adsSpendUtils"

// Format numbers with commas and no decimal places
export const formatNumberWithCommas = (num: number): string => {
  return num.toLocaleString("en-US", {
    maximumFractionDigits: 0,
  })
}

// Calculate leads needed to break even (cover total spent)
export const calculateLeadsToBreakeven = (
  totalSpent: number,
  averageJobValue: number = 1000
): number => {
  // If average job value is 0 or undefined, return default value
  if (!averageJobValue) return 1

  // Calculate how many leads are needed
  const leadsNeeded = Math.ceil(totalSpent / averageJobValue)

  // Return at least 1 lead
  return Math.max(1, leadsNeeded)
}

// Calculate metrics based on leads data and timeframe
export const calculateMetrics = async (
  leadsData: Lead[],
  businessId: string,
  timeFrame: string = DEFAULT_TIMEFRAME
) => {
  // Ensure timeFrame is a valid TimeFrameOption
  const validTimeFrame = validateTimeFrame(timeFrame)

  // Add debug logging
  console.log(`Calculating metrics for timeframe: ${validTimeFrame}`)
  console.log(`Total leads before filtering: ${leadsData.length}`)

  // Filter leads based on timeframe using the shared utility
  const timeFilteredLeads = filterLeadsByTimeFrame(leadsData, validTimeFrame)
  console.log(
    `Filtered leads for ${validTimeFrame}: ${timeFilteredLeads.length}`
  )

  // Get total ad spend based on timeframe
  const totalSpent = await calculateTotalAdSpend(businessId, validTimeFrame)
  console.log(`Total ad spend for ${validTimeFrame}: ${totalSpent}`)

  const defaultGrossProfitPerJob = 1000 // Default value if a lead doesn't have a value

  // Calculate disqualified leads
  const disqualifiedLeads = timeFilteredLeads.filter(
    (lead) =>
      lead.outcome?.startsWith("Disqualified") ||
      lead.outcome === "Existing Customer"
  )

  const totalLeads = timeFilteredLeads.length
  const nonDisqualifiedLeads = totalLeads - disqualifiedLeads.length

  // Calculate calls answered percentage - only count phone call leads that aren't disqualified
  const phoneCallLeads = timeFilteredLeads.filter(
    (lead) =>
      lead.type === "Call" &&
      (!lead.outcome?.startsWith("Disqualified") ||
        lead.outcome !== "Existing Customer")
  )

  const totalCallsAnswered = phoneCallLeads.filter(
    (lead) => lead.status === "Call Answered"
  ).length

  const phoneCallLeadsCount = phoneCallLeads.length

  // Avoid division by zero
  const callsAnsweredPercentage =
    phoneCallLeadsCount > 0
      ? Math.round((totalCallsAnswered / phoneCallLeadsCount) * 100)
      : 0

  // Log values for debugging
  console.log("Phone call leads count:", phoneCallLeadsCount)
  console.log("Total calls answered:", totalCallsAnswered)
  console.log("Calls answered percentage:", callsAnsweredPercentage)

  const closedDeals = timeFilteredLeads.filter(
    (lead) => lead.outcome === "Sale Won"
  )

  const closedDealsCount = closedDeals.length
  console.log("Closed deals count:", closedDealsCount)

  // Calculate close rate
  const closeRate =
    nonDisqualifiedLeads > 0
      ? Math.round((closedDealsCount / nonDisqualifiedLeads) * 100)
      : 0

  // Calculate Gross Profit
  const grossProfit = closedDeals.reduce((total, lead) => {
    if (lead.value) {
      // Strip non-numeric characters and convert to number
      const value = Number(lead.value.replace(/[^0-9.-]+/g, ""))
      return total + (isNaN(value) ? defaultGrossProfitPerJob : value)
    }
    return total + defaultGrossProfitPerJob
  }, 0)
  console.log("Gross profit:", grossProfit)

  // Calculate ROI (Return on Investment)
  const roi = grossProfit - totalSpent
  console.log("ROI:", roi)

  // Filter leads after April 1st, 2025 00:00 GMT
  const april2025Cutoff = new Date('2025-04-01T00:00:00Z')
  const leadsAfterApril2025 = timeFilteredLeads.filter(lead => {
    const leadDate = parseLeadDate(lead.date)
    return (
      leadDate &&
      leadDate >= april2025Cutoff &&
      (!lead.outcome?.startsWith("Disqualified") &&
        lead.outcome !== "Existing Customer")
    )
  })
  const leadsAfterApril2025Count = leadsAfterApril2025.length
  console.log("Leads since April 1st, 2025:", leadsAfterApril2025Count)

  // Calculate cost per lead (using only leads after April 1st, 2025)
  const costPerLead =
    leadsAfterApril2025Count > 0 ? Math.round(totalSpent / leadsAfterApril2025Count) : 0
  console.log("Cost per lead:", costPerLead)

  // Calculate leads to breakeven
  const leadsToBreakeven = calculateLeadsToBreakeven(
    totalSpent,
    grossProfit / Math.max(1, closedDealsCount)
  )

  return {
    nonDisqualifiedLeads,
    totalSpent,
    costPerLead,
    callsAnsweredPercentage,
    closedDealsCount,
    grossProfit,
    roi,
    phoneCallLeads,
    leadsToBreakeven,
    closeRate,
  }
}
